import Skeleton from '../../components/layout/Skeleton';
import { useQuery } from 'react-query';
import axios from 'axios';
import HBox from '../../components/layout/HBox';
import VBox from '@/components/layout/VBox';
import { translateFilter } from './util';
import { useState } from 'react';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import Botao from '../../components/Botao';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const INTENCOES_PRINCIPAIS = [
    'Suporte Técnico',
    'Informação',
    'Reclamação',
    'Cancelamento',
    'Compra/Venda',
    'Agendamento',
    'Financeiro',
    'Outros',
] as const;

const TOP_N_PADRAO = 10;
const TOP_N_ALTERNAR = [10, 20, 30];

const ORIGEM_OPTIONS = [
    { id: '', label: 'Todas as origens' },
    { id: 'humano', label: 'Só atendimento humano' },
    { id: 'chatbot', label: 'Só chatbot' },
];

const truncar = (label: string, max: number) =>
    label.length > max ? label.slice(0, max - 1) + '…' : label;

export const GraficoIntencaoSecundaria = ({ colorPalette, periodo, filterCanal }: any) => {
    const [intencaoPrincipal, setIntencaoPrincipal] = useState<string>('');
    const [topN, setTopN] = useState<number>(TOP_N_PADRAO);
    const [origem, setOrigem] = useState<string>('');

    const q = useQuery(
        ['intencao_secundaria', periodo, filterCanal, intencaoPrincipal, origem],
        async () => {
            const filtro: any = JSON.parse(
                translateFilter({ periodo, canal_id: filterCanal })
            );
            if (intencaoPrincipal) {
                filtro.intencao_principal = ['in', [intencaoPrincipal]];
            }
            if (origem === 'humano') {
                filtro.atendimento_humano = ['eq', true];
            } else if (origem === 'chatbot') {
                filtro.atendimento_humano = ['eq', false];
            }
            const qs = new URLSearchParams();
            qs.append('facts', 'total');
            qs.append('dimensions', 'intencao_secundaria');
            qs.append('order', '2 desc');
            qs.append('filter', JSON.stringify(filtro));
            const res = await axios.get('/api/relatorios/chat_resumo?' + qs.toString());
            return res.data.result;
        },
        { refetchOnWindowFocus: false }
    );

    const linhas: any[] = q.data ?? [];
    const total = linhas.reduce((s: number, r: any) => s + parseInt(r.total), 0);

    // Top-N + "Outros": as N maiores intenções (agrupadas pelo canônico do
    // catálogo) com nome próprio; o resto da cauda vira um único grupo "Outros".
    const topItens = linhas.slice(0, topN).map((r: any) => ({
        nome: r.intencao_secundaria,
        total: parseInt(r.total),
    }));
    const gruposNaCauda = linhas.length - topItens.length;
    const cauda = linhas.slice(topN).reduce(
        (s: number, r: any) => s + parseInt(r.total),
        0
    );
    // Se um item real "Outros" já está no topo, a cauda se soma a ele em vez de
    // criar uma segunda barra com o mesmo rótulo.
    const dados = [...topItens];
    if (cauda > 0) {
        const existente = dados.find((d: any) => d.nome === 'Outros');
        if (existente) existente.total += cauda;
        else dados.push({ nome: 'Outros', total: cauda });
    }

    const corDaBarra = (nome: string, i: number) =>
        nome === 'Outros'
            ? '#6b7280'
            : colorPalette[i % colorPalette.length];

    const TooltipTop = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const { nome, total: v } = payload[0].payload;
            const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        padding: '0.5rem',
                    }}
                >
                    <p style={{ margin: 0, fontWeight: 'bold', maxWidth: 220 }}>
                        {nome}
                    </p>
                    <p style={{ margin: 0 }}>{v} chats · {pct}%</p>
                </div>
            );
        }
        return null;
    };

    if (q.isFetching) return <Skeleton format="box" />;

    return (
        <VBox>
            <HBox>
                <CampoSeletor
                    noSearch
                    placeholder="Origem"
                    options={ORIGEM_OPTIONS}
                    value={origem}
                    onChange={(value: string | null) => setOrigem(value ?? '')}
                    style={{ width: 160, flex: '0 0 160px' }}
                />
                <CampoSeletor
                    noSearch
                    placeholder="Intenção principal"
                    options={[
                        { id: '', label: 'Todas as intenções' },
                        ...INTENCOES_PRINCIPAIS.map(i => ({ id: i, label: i })),
                    ]}
                    value={intencaoPrincipal}
                    onChange={(value: string | null) => setIntencaoPrincipal(value ?? '')}
                    style={{ width: '100%', flex: 1 }}
                />
            </HBox>
            <HBox>
                {TOP_N_ALTERNAR.map(n => (
                    <Botao
                        key={n}
                        variant={topN === n ? 'outline-primary' : 'outline-secondary'}
                        onClick={() => setTopN(n)}
                        title={`Mostrar as ${n} intenções mais frequentes`}
                    >
                        Top-{n}
                    </Botao>
                ))}
            </HBox>

            {total === 0 && (
                <VBox center style={{ flex: 1 }}>
                    <p
                        style={{
                            fontSize: '0.875rem',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        {q.data && q.data.length === 0
                            ? 'Nenhum dado encontrado para o período selecionado.'
                            : 'Sem dados suficientes para o período selecionado.'}
                    </p>
                </VBox>
            )}

            {total > 0 && (
                <>
                    <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={dados}
                                layout="vertical"
                                margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} />
                                <YAxis
                                    type="category"
                                    dataKey="nome"
                                    width={155}
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(v: string) => truncar(v, 20)}
                                />
                                <Tooltip
                                    cursor={{ fill: '#8882' }}
                                    itemStyle={{ padding: 0, fontSize: '0.9rem' }}
                                    content={<TooltipTop />}
                                />
                                <Bar dataKey="total" name="Chats" radius={[0, 3, 3, 0]}>
                                    {dados.map((d: any, i: number) => (
                                        <Cell key={d.nome} fill={corDaBarra(d.nome, i)} />
                                    ))}
                                    <LabelList dataKey="total" position="right" />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <p
                        style={{
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        {gruposNaCauda > 0
                            ? `Abra "Visualizar dados" para explorar "Outros".` : ''}
                    </p>
                </>
            )}
        </VBox>
    );
};
